import {
  ApartmentOutlined,
  CheckSquareOutlined,
  MailOutlined,
  NodeIndexOutlined,
  ReadOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import styles from "./styles.module.css";

/* What each shared type is called and what it looks like. The API deliberately
 * returns identifiers rather than URLs — which panel route shows a task is a
 * frontend fact that changes with the frontend — so the routing lives here, in
 * one place, for both the composer chip and the message bubble. */
const TYPES = {
  project: {
    label: "پروژه",
    icon: <ApartmentOutlined />,
    href: (entity) => `/projects/edit/${entity.project_uuid}`,
  },
  task: {
    label: "تسک",
    icon: <CheckSquareOutlined />,
    href: (entity) =>
      entity.project_uuid
        ? `/projects/edit/${entity.project_uuid}?tab=Kanban&task=${entity.uuid}`
        : null,
  },
  sprint: {
    label: "چرخه",
    icon: <NodeIndexOutlined />,
    href: (entity) =>
      entity.project_uuid
        ? `/projects/edit/${entity.project_uuid}?tab=Sprints`
        : null,
  },
  doc: {
    label: "سند",
    icon: <ReadOutlined />,
    href: (entity) =>
      entity.project_uuid ? `/projects/edit/${entity.project_uuid}?tab=Docs` : null,
  },
  letter: {
    label: "نامه",
    icon: <MailOutlined />,
    href: (entity) => `/letters?letter=${entity.uuid}`,
  },
  customer: {
    label: "مشتری",
    icon: <TeamOutlined />,
    href: () => "/customers",
  },
};

export const CHAT_ENTITY_TYPES = TYPES;

/**
 * A pointer to something in the panel, sitting inside a chat message.
 *
 * The title is whatever the API resolved just now, not what it was called when
 * it was shared — a task that gets renamed reads correctly in an old thread.
 * When the reference no longer resolves the API sends nothing and the message
 * is just its text, so there is no "deleted item" tombstone to render here.
 */
const ChatEntityCard = ({
  entity,
  onRemove,
  compact = false,
  /* Off in the picker: there the card is the choice being made, and a link
     would navigate away from the message the person is composing instead of
     attaching to it. */
  asLink = true,
}) => {
  if (!entity) return null;

  const config = TYPES[entity.type];
  if (!config) return null;

  const href = config.href(entity);
  /* A subtask is a task with a parent on the API side; the distinction only
   * exists for the reader, so it is made here rather than in the type list. */
  const label =
    entity.type === "task" && entity.is_subtask ? "زیرتسک" : config.label;

  const body = (
    <>
      <span className={styles.icon}>{config.icon}</span>
      <span className={styles.body}>
        <span className={styles.kind}>{label}</span>
        <span className={styles.title}>{entity.title}</span>
        {entity.context && (
          <span className={styles.context}>{entity.context}</span>
        )}
      </span>
    </>
  );

  const className = `${styles.card} ${compact ? styles.cardCompact : ""}`;

  if (onRemove) {
    return (
      <div className={className}>
        {body}
        <button
          type="button"
          className={styles.remove}
          onClick={onRemove}
          aria-label="حذف مورد پیوست‌شده"
        >
          ×
        </button>
      </div>
    );
  }

  /* Without a resolvable route — a task whose project the reader cannot see —
   * the card still says what was shared, it just is not a link. */
  return asLink && href ? (
    <a className={`${className} ${styles.cardLink}`} href={href}>
      {body}
    </a>
  ) : (
    <div className={className}>{body}</div>
  );
};

export default ChatEntityCard;
